import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import PerformanceExhibit from "./performance-exhibit";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";

import MetaTags from "@/components/MetaTags";
import PageHeader from "@/components/page-header";
import BookButton from "@/components/ui/BookButton";
import Subtitle from "@/components/ui/subtitle";

function CaseStudySchema({ caseStudy }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: caseStudy.title,
    description: caseStudy.metaDescription,
    mainEntityOfPage: `https://www.venturawebdesign.com${caseStudy.path}`,
    ...(caseStudy.image
      ? { image: `https://www.venturawebdesign.com${caseStudy.image.src}` }
      : {}),
    about: caseStudy.services,
    author: {
      "@type": "Organization",
      name: "Ventura Web Design & Marketing",
      url: "https://www.venturawebdesign.com",
    },
    publisher: {
      "@type": "Organization",
      name: "Ventura Web Design & Marketing",
      url: "https://www.venturawebdesign.com",
    },
  };

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
    </Head>
  );
}

function EditorialSection({ eyebrow, heading, children }) {
  return (
    <section>
      <Subtitle subtitle={eyebrow} icon={false} custom="mb-3" />
      <div className="space-y-5 text-lg leading-8 text-black">
        <p>{heading}</p>
        {children}
      </div>
    </section>
  );
}

export default function CaseStudyTemplate({ caseStudy }) {
  return (
    <>
      <MetaTags
        pageTitle={caseStudy.title}
        pageDescription={caseStudy.metaDescription}
        path={caseStudy.path}
        image={caseStudy.image?.src}
        type="article"
      />
      <CaseStudySchema caseStudy={caseStudy} />
      <article className="bg-white text-ups-text">
        <header className="mx-auto max-w-screen-xl px-5 md:px-10">
          <Link
            href="/case-studies"
            className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-vblue3 hover:underline"
          >
            ← All case studies
          </Link>
          <PageHeader
            eyebrow="Case Study"
            title={caseStudy.title}
            description={caseStudy.description}
            customCss="!max-w-5xl pb-8 pt-12"
            headingClassName="text-4xl !leading-tight sm:text-[2.75rem] lg:text-5xl"
            descriptionClassName="!text-black"
          />

          {caseStudy.exhibit?.format !== "facts" && (
            <section aria-label="Project at a glance">
              <dl className="mx-auto grid max-w-4xl grid-cols-2 gap-x-6 gap-y-6 text-center sm:grid-cols-4 sm:gap-8">
                {caseStudy.stats.map((stat) => (
                  <div key={stat.label}>
                    <dd className="text-[clamp(1.5rem,2vw,2rem)] font-bold leading-none text-black">
                      {stat.value}
                    </dd>
                    <dt className="mx-auto mt-1.5 max-w-[11rem] text-xs leading-snug text-black sm:text-sm">
                      {stat.label}
                    </dt>
                  </div>
                ))}
              </dl>
            </section>
          )}
        </header>

        <div className="mx-auto my-12 max-w-screen-xl px-5 md:px-10">
          {caseStudy.exhibit ? (
            <PerformanceExhibit exhibit={caseStudy.exhibit} />
          ) : (
            <Image
              src={caseStudy.image.src}
              alt={caseStudy.image.alt}
              width={caseStudy.image.width}
              height={caseStudy.image.height}
              sizes="(max-width: 1280px) 100vw, 1240px"
              className="h-auto w-full rounded-3xl shadow-sm ring-1 ring-neutral-900/5"
              priority
            />
          )}
        </div>

        <div className="mx-auto grid max-w-screen-xl gap-14 px-5 pb-0 md:px-10 lg:grid-cols-[minmax(0,1fr)_minmax(15rem,20rem)] lg:gap-10">
          <div>
            <h2 className="text-2xl font-bold text-black sm:text-3xl">
              What we did
            </h2>

            <div className="mt-10 space-y-14 sm:space-y-16">
              <EditorialSection
                eyebrow={caseStudy.overview.eyebrow}
                heading={caseStudy.overview.heading}
              >
                {caseStudy.overview.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </EditorialSection>

              <EditorialSection
                eyebrow={caseStudy.issue.eyebrow}
                heading={caseStudy.issue.heading}
              >
                {caseStudy.issue.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </EditorialSection>

              <EditorialSection
                eyebrow={caseStudy.solution.eyebrow}
                heading={caseStudy.solution.heading}
              >
                {caseStudy.solution.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                <ul className="list-disc space-y-3 pl-6 marker:text-vblue3">
                  {caseStudy.solution.highlights.map((highlight) => (
                    <li key={highlight}>{highlight}</li>
                  ))}
                </ul>
              </EditorialSection>

              <EditorialSection
                eyebrow={caseStudy.outcome.eyebrow}
                heading={caseStudy.outcome.heading}
              >
                {caseStudy.outcome.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                {caseStudy.outcome.followUp && (
                  <p className="border-l-4 border-vblue3 bg-blue-50/60 px-6 py-5 text-base leading-7 text-black">
                    {caseStudy.outcome.followUp}
                  </p>
                )}
              </EditorialSection>
            </div>
            <section className="mt-8">
              <h2 className="text-xl font-bold text-black">
                Explore the Services Behind This Project
              </h2>
              <div className="mt-5">
                {caseStudy.relatedServices.map((service) => (
                  <Link
                    key={service.href}
                    href={service.href}
                    className="group flex items-center justify-between gap-3 border-t border-slate-200 py-3 text-sm font-semibold leading-5 text-black transition-colors hover:border-vblue3 hover:text-vblue3"
                  >
                    <span>{service.label}</span>
                    <ArrowUpRight
                      aria-hidden="true"
                      className="size-4 shrink-0 text-slate-400 transition-colors group-hover:text-vblue3"
                    />
                  </Link>
                ))}
              </div>
            </section>
          </div>

          <aside className="h-fit lg:sticky lg:top-[145px]">
            <h2 className="text-xl font-bold text-black">Services provided</h2>
            <ul className="mt-5 space-y-3">
              {caseStudy.services.map((service) => (
                <li
                  key={service}
                  className="flex items-start gap-3 text-sm leading-6 text-black"
                >
                  <Check
                    aria-hidden="true"
                    className="mt-1 size-4 shrink-0 text-vblue3"
                    strokeWidth={2.5}
                  />
                  <span>{service}</span>
                </li>
              ))}
            </ul>
          </aside>
        </div>

        <div aria-hidden="true" className="mx-auto py-14 sm:py-20">
          <div className="mx-auto h-1 w-16 rounded-full bg-ups-primary" />
        </div>

        <section className="mx-auto max-w-screen-xl px-5 pt-0 md:px-10">
          <div className="flex flex-col items-center justify-between gap-8 sm:flex-row">
            <div className="max-w-2xl text-center sm:text-left">
              <h2 className="text-2xl font-bold text-black sm:text-3xl">
                {caseStudy.cta.heading}
              </h2>
              <p className="mt-3 text-base leading-7 text-black">
                {caseStudy.cta.description}
              </p>
            </div>
            <BookButton
              color="blue"
              text={caseStudy.cta.primaryLabel}
              link={caseStudy.cta.primaryHref}
              right
              icon={<ArrowRight aria-hidden="true" className="size-4" />}
              custom="shrink-0 text-center"
            />
          </div>
        </section>
      </article>
    </>
  );
}
